import type {
  NotificationCtpCategory,
  NotificationCtpPreference,
  NotificationCtpPreferenceGroup,
} from 'src/types/graphql';

// Notification Categories
export const categoryUpdates: NotificationCtpCategory = {
  id: 'updates',
  label: 'Application updates',
  description: 'Updates about the jobs you’ve applied for',
  __typename: 'NotificationCTPCategory',
};

export const categoryOpportunities: NotificationCtpCategory = {
  id: 'opportunities',
  label: 'Job opportunities',
  description: 'We’ll send you jobs to help you find your next role',
  __typename: 'NotificationCTPCategory',
};

export const categoryContent: NotificationCtpCategory = {
  id: 'content',
  label: 'Your content',
  description: 'Updates about your contributions',
  __typename: 'NotificationCTPCategory',
};

export const categoryGetTheMost: NotificationCtpCategory = {
  id: 'getTheMost',
  label: 'Get the most out of SEEK',
  description: 'Ensure you’re getting the full benefit from our products',
  __typename: 'NotificationCTPCategory',
};

export const categoryAdvice: NotificationCtpCategory = {
  id: 'advice',
  label: 'Advice and insights',
  description: 'Tips for your career',
  __typename: 'NotificationCTPCategory',
};

export const categoryResearch: NotificationCtpCategory = {
  id: 'research',
  label: 'Surveys and research',
  description: 'Tell us how we can improve our products and services',
  __typename: 'NotificationCTPCategory',
};

/**
 * Notification Frequency Options
 */
export const notificationCTPFrequencyOptions: Array<{
  __typename?: 'NotificationCTPFrequencyOptions' | undefined;
  id: string;
  label: string;
  description: string | null;
}> = [
  {
    id: 'dynamic',
    label: 'Dynamic',
    description:
      'More frequent emails when you’re actively looking and less when you’re not.',
    __typename: 'NotificationCTPFrequencyOptions',
  },
  {
    id: 'daily',
    label: 'Daily',
    description: null,
    __typename: 'NotificationCTPFrequencyOptions',
  },
  {
    id: 'weekly',
    label: 'Weekly',
    description: null,
    __typename: 'NotificationCTPFrequencyOptions',
  },
];

/**
 * Notification Preferences 1
 *
 * Application updates
 */

// 1. Application updates > AppConfirmed
export const AppConfirmedPreference: NotificationCtpPreference = {
  product: {
    id: 'AppConfirmed',
    label: 'Application confirmation',
    description:
      'Receive confirmation when your application is successfully sent to the employer',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'AppConfirmed:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 2. Application updates > AppViewed
export const AppViewedPreference: NotificationCtpPreference = {
  product: {
    id: 'AppViewed',
    label: 'Application viewed',
    description: 'Get notified when an employer views your application',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'AppViewed:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
    {
      id: 'AppViewed:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 3. Application updates > AppNotSuitable
export const AppNotSuitablePreference: NotificationCtpPreference = {
  product: {
    id: 'AppNotSuitable',
    label: 'Application unlikely to progress',
    description:
      'Be informed if the employer indicates your application is unlikely to progress further',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'AppNotSuitable:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
    {
      id: 'AppNotSuitable:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 4. Application updates > AppAdExpired
export const AppAdExpiredPreference: NotificationCtpPreference = {
  product: {
    id: 'AppAdExpired',
    label: 'Job ad expired',
    description:
      'Receive an alert when a job you’ve applied for is no longer advertised',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'AppAdExpired:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 5. Application updates > AppliedJobsActivity
export const AppliedJobsActivityPreference: NotificationCtpPreference = {
  product: {
    id: 'AppliedJobsActivity',
    label: 'Applied jobs activity',
    description:
      'Find out if employers have recently processed applications in jobs you applied to.',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'AppliedJobsActivity:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 6. Application updates > VerificationRequests
export const VerificationRequestsPreference: NotificationCtpPreference = {
  product: {
    id: 'VerificationRequests',
    label: 'Verification Requests',
    description:
      'Be notified when there are credentials you can verify to strengthen your application',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'VerificationRequests:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

/**
 * Notification Preferences 2
 *
 * Job opportunities
 */

// 1. Job opportunities > WRU
export const WRUPreference: NotificationCtpPreference = {
  product: {
    id: 'WRU',
    label: 'Recommended jobs',
    description:
      'Receive personalised job recommendations based on your profile and recent activity',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'WRU:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: {
        value: 'dynamic',
        options: notificationCTPFrequencyOptions,
        __typename: 'NotificationCTPFrequency',
      },
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 2. Job opportunities > NewJob
export const NewJobPreference: NotificationCtpPreference = {
  product: {
    id: 'NewJob',
    label: 'Jobs just for you',
    description:
      'Get the latest jobs recommended for you, sent straight to your mobile',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'NewJob:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 3. Job opportunities > TopApplicant
export const TopApplicantPreference: NotificationCtpPreference = {
  product: {
    id: 'TopApplicant',
    label: 'Top candidate alerts',
    description: 'Receive alerts when you’re a top candidate for a job opening',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'TopApplicant:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 4. Job opportunities > RTA
export const ReminderToApplyPreference: NotificationCtpPreference = {
  product: {
    id: 'ReminderToApply',
    label: 'Reminder to apply',
    description:
      'Get a reminder if you haven’t applied for a saved job within a week',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'ReminderToApply:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: false,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
    {
      id: 'ReminderToApply:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 5. Job opportunities > SavedSearch
export const SavedSearchPreference: NotificationCtpPreference = {
  product: {
    id: 'SavedSearch',
    label: 'Saved search job alerts',
    description: 'The latest jobs that match your saved searches',
    action: {
      label: 'Manage your saved searches',
      path: '/my-activity/saved-searches',
    },
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'SavedSearch:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
    {
      id: 'SavedSearch:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: false,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 6. Job opportunities > SearchesToSave
export const SearchesToSavePreference: NotificationCtpPreference = {
  product: {
    id: 'SearchesToSave',
    label: 'Searches you should save',
    description:
      'Receive suggestions on searches you should save to receive relevant job alerts',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'SearchesToSave:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

/**
 * Notification Preferences 3
 *
 * Your content
 */

// 1. Your content > CareerHubThreadReply
export const CareerHubThreadReplyPreference: NotificationCtpPreference = {
  product: {
    id: 'CareerHubThreadReply',
    label: 'Career Hub activity updates',
    description:
      'Be notified when there is new activity on your threads in Career Hub',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'CareerHubThreadReply:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

/**
 * Notification Preferences 4
 *
 * Get the most
 */

// 1. Get the most > MarketingProfileUpdate
export const MarketingProfileUpdatePreference: NotificationCtpPreference = {
  product: {
    id: 'MarketingProfileUpdate',
    label: 'Profile tips',
    description:
      'Occasional advice to help ensure your profile is working for you',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'MarketingProfileUpdate:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
    {
      id: 'MarketingProfileUpdate:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 2. Get the most > ProfileActivity
export const ProfileActivityPreference: NotificationCtpPreference = {
  product: {
    description:
      'Stay updated on how employers engage with your profile - see searches, views, downloads, and more',
    id: 'ProfileActivity',
    label: 'Profile activity',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      channel: {
        label: 'Email',
        id: 'email',
        __typename: 'NotificationCTPChannel',
      },
      frequency: null,
      id: 'ProfileActivity:email',
      subscribed: true,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 3. Get the most > MarketingProductUpdate
export const MarketingProductUpdatePreference: NotificationCtpPreference = {
  product: {
    id: 'MarketingProductUpdate',
    label: 'Product updates',
    description: 'Learn about Seek products to help you on your career journey',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'MarketingProductUpdate:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
    {
      id: 'MarketingProductUpdate:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

/**
 * Notification Preferences 5
 *
 * Advice and insights
 */

// 1. Advice and insights > CareerHubReengageExplore
export const CareerHubReengageExplorePreference: NotificationCtpPreference = {
  product: {
    id: 'CareerHubReengageExplore',
    label: 'Personalised Career Hub content',
    description:
      'We’ll notify you when we match career content to your profile',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'CareerHubReengageExplore:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 2. Advice and insights > MarketingAdviceTips
export const MarketingAdviceTipsPreference: NotificationCtpPreference = {
  product: {
    id: 'MarketingAdviceTips',
    label: 'Career advice',
    description:
      'The latest career advice, industry trends, and tips to get you where you’re going',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'MarketingAdviceTips:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
    {
      id: 'MarketingAdviceTips:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 3. Advice and insights > MarketingPartnerUpdates
export const MarketingPartnerUpdatesPreference: NotificationCtpPreference = {
  product: {
    id: 'MarketingPartnerUpdates',
    label: 'Partner updates',
    description: 'Information and updates from our partner businesses',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'MarketingPartnerUpdates:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
    {
      id: 'MarketingPartnerUpdates:push',
      channel: {
        id: 'push',
        label: 'Push notifications',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// 4. Advice and insights > MarketingNewsletter
export const MarketingNewsletterPreference: NotificationCtpPreference = {
  product: {
    id: 'MarketingNewsletter',
    label: 'Newsletters',
    description: 'The latest industry news',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'MarketingNewsletter:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: true,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

/**
 * Notification Preferences 6
 *
 * Surveys and research
 */

// 1. Surveys and research > MarketingSurveyResearch
export const MarketingSurveyResearchPreference: NotificationCtpPreference = {
  product: {
    id: 'MarketingSurveyResearch',
    label: 'Requests for your input',
    description: 'Tell us how we can improve our products and services',
    action: null,
    __typename: 'NotificationCTPProduct',
  },
  subscriptions: [
    {
      id: 'MarketingSurveyResearch:email',
      channel: {
        id: 'email',
        label: 'Email',
        __typename: 'NotificationCTPChannel',
      },
      subscribed: false,
      frequency: null,
      token: 'token',
      __typename: 'NotificationCTPSubscription',
    },
  ],
  __typename: 'NotificationCTPPreference',
};

// Notification Preference Groups
export const notificationCTPPreferenceGroups: NotificationCtpPreferenceGroup[] =
  [
    {
      category: categoryOpportunities,
      preferences: [
        WRUPreference,
        NewJobPreference,
        TopApplicantPreference,
        ReminderToApplyPreference,
        SavedSearchPreference,
        SearchesToSavePreference,
      ],
      __typename: 'NotificationCTPPreferenceGroup',
    },
    {
      category: categoryUpdates,
      preferences: [
        AppConfirmedPreference,
        AppViewedPreference,
        AppNotSuitablePreference,
        AppAdExpiredPreference,
        AppliedJobsActivityPreference,
        VerificationRequestsPreference,
      ],
      __typename: 'NotificationCTPPreferenceGroup',
    },
    {
      category: categoryContent,
      preferences: [CareerHubThreadReplyPreference],
      __typename: 'NotificationCTPPreferenceGroup',
    },
    {
      category: categoryGetTheMost,
      preferences: [
        MarketingProfileUpdatePreference,
        ProfileActivityPreference,
        MarketingProductUpdatePreference,
      ],
      __typename: 'NotificationCTPPreferenceGroup',
    },
    {
      category: categoryAdvice,
      preferences: [
        MarketingAdviceTipsPreference,
        CareerHubReengageExplorePreference,
        MarketingPartnerUpdatesPreference,
        MarketingNewsletterPreference,
      ],
      __typename: 'NotificationCTPPreferenceGroup',
    },
    {
      category: categoryResearch,
      preferences: [MarketingSurveyResearchPreference],
      __typename: 'NotificationCTPPreferenceGroup',
    },
  ];
